import { Dispatch, SetStateAction, useContext } from "react";
import { Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { BoxFull, Button } from "iuvity-ui-components";
import { CustomInput } from "../inputs/CustomInput";
import { DialogProps } from "../dialog/CustomDialog";
import { ProdGeneralInfoFormSchema } from "../../validations/schemas";
import { propertyKeyLabel } from "../../utils";
import { REVALIDATE_FORM_PROPS } from "../../constants";
import { AppContext } from "../../context/index";
import { PrivilegeValidatorComponent } from "../shared/PrivilegeValidatorComponent";

interface ProdGeneralInfoProps {
  data: any;
  form: any;
  fieldActionFn: Function;
  auxModalController: React.Dispatch<React.SetStateAction<DialogProps>>;
  setActiveIndex: Dispatch<SetStateAction<number>>;
}

export const ProdConfigsInfo: React.FC<ProdGeneralInfoProps> = ({
  data,
  form,
  fieldActionFn,
  auxModalController,
  setActiveIndex,
}) => {
  const {
    t,
    appState: { user, permissiondInfoByRole },
    setAppState,
  } = useContext(AppContext);

  const {
    control,
    getValues,
    setValue,
    reset,
    formState: { errors, dirtyFields, isDirty, isValid },
  } = form;

  const onSave = async () => {
    const formValues = getValues();
    const newValues = Object.keys(dirtyFields).map((key) => {
      const keyInfo = data.find(
        (prop: any) => prop.key.split(".").pop() === key
      );
      return {
        id: keyInfo!.id,
        key: keyInfo!.key,
        value: `${formValues[key]}`,
        updatedby: user?.name,
      };
    });
    const actionStatus = await fieldActionFn("UPDATE", newValues);
    if (actionStatus === "SUCCESS") reset(formValues);
  };

  const handleModalProps = (isCancelButton?: boolean) => {
    auxModalController((prev) => ({
      ...prev,
      visible: true,
      title: isCancelButton
        ? t("GlobalConfigurations.cancelModalTitle")
        : t("GlobalConfigurations.saveModalTitle"),
      content: isCancelButton
        ? t("GlobalConfigurations.cancelModalContent")
        : t("GlobalConfigurations.saveModalContent"),
      onConfirm: isCancelButton ? onCancel : onSave,
    }));
  };

  const onCancel = () => {
    setAppState((prev) => ({
      ...prev,
      functionalityCode: '/products',
    }));
    reset();
    setActiveIndex(0);
  };

  return (
    <BoxFull className="dt-c mt-4 py-5 px-5">
      <div className="flex align-items-flex-start justify-content-flex-start flex-wrap">
        {Object.keys(getValues()).length > 0 && (
          <>
            {data?.map((property: any) => {
              const newKey = property.key.split(".").pop();
              return (
                <div
                  key={`c-${property.key}`}
                  className="flex flex-column mb-3"
                  style={{ width: "50%" }}
                >
                  <Controller
                    key={newKey}
                    name={newKey}
                    control={control}
                    rules={(ProdGeneralInfoFormSchema(t) as any)[newKey]}
                    render={({ field }) => {
                      if (property.type === "boolean") {
                        field.onChange = () => {
                          setValue(
                            field.name,
                            `${!(field.value === "true")}`,
                            REVALIDATE_FORM_PROPS
                          );
                        };
                      }
                      let extraProps = {
                        keyfilter: (ProdGeneralInfoFormSchema(t) as any)[newKey]
                          ?.keyfilter,
                        maxLength: (ProdGeneralInfoFormSchema(t) as any)[newKey]
                          ?.maxLength?.value,
                      };
                      const label =
                        propertyKeyLabel(t, "Products.", newKey) || "";
                      return (
                        <CustomInput
                          id={`input-${newKey}`}
                          key={newKey}
                          className={`${
                            (errors as any)[newKey]?.message
                              ? "p-invalid"
                              : "input-md mb-4"
                          }`}
                          label={label}
                          placeholder={`${t(
                            "Products.propPlaceholder"
                          )} ${label.toLowerCase()}`}
                          type={
                            property.type === "number" ? "text" : property.type
                          }
                          autoComplete="off"
                          style={{
                            width: "20rem",
                          }}
                          {...field}
                          {...extraProps}
                        />
                      );
                    }}
                  />
                  {property.type !== "boolean" && (
                    <ErrorMessage
                      key={`err-${newKey}`}
                      errors={errors}
                      name={newKey}
                      //@ts-ignore
                      render={({ message }) => (
                        <small
                          className="p-error block"
                          style={{
                            marginTop: "0.5rem",
                            marginBottom: "1.5rem",
                          }}
                        >
                          {message}
                        </small>
                      )}
                    />
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
      <div className="ap-selector-btn-c mt-2 gap-5">
        <Button
          variant="p-button-link"
          label={t("Products.cancelBtnLabel")}
          onClick={!isDirty ? onCancel : () => handleModalProps(true)}
        />
        <PrivilegeValidatorComponent showButton={permissiondInfoByRole.UPDATE} >
          <Button
            variant="p-button"
            label={t("Products.saveBtnLabel")}
            disabled={!(isDirty && isValid)}
            onClick={() => handleModalProps()}
          />
        </PrivilegeValidatorComponent>
      </div>
    </BoxFull>
  );
};
